import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Chip } from "primereact/chip";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useGetSftpPartnerQuery,
  useSetSftpPartnerEnabledMutation,
} from "store/queries/sftpPartners";
import {
  ISftpPartnerDetail,
  ISftpPartnerMappingRule,
  ISftpPartnerRecentConversion,
  SftpIngestKind,
} from "types/SftpPartner";

import { OutboundReportsCard } from "./components/OutboundReports/OutboundReportsCard";
import {
  SftpAccountName,
  SftpRuleSettings,
} from "./components/SftpPartnerParts";
import { SftpStatusTag } from "./components/SftpPartnersTab/SftpPartnersTab";
import { formatDateTime } from "./utils/hstImports";
import {
  claimTypeLabel,
  humanizeEnum,
  ingestKindLabel,
  ingestModeLabel,
  SFTP_TAB_PATH,
  sftpApiErrorMessage,
  sftpPartnerEditPath,
} from "./utils/sftpPartners";

// What enabling / disabling a partner does depends on which job reads its inbound files.
const TOGGLE_MESSAGES: Record<
  SftpIngestKind,
  { enable: string; disable: string }
> = {
  EDI: {
    disable:
      "The daily SFTP job stops loading this partner's files. Files keep arriving in S3 and are loaded when the partner is re-enabled.",
    enable:
      "The daily SFTP job starts loading this partner's files again, including every file that arrived while it was disabled.",
  },
  ATHENA_ORDERS: {
    disable:
      "The daily Athena order import stops loading this partner's files. Files keep arriving in S3 and are loaded when the partner is re-enabled.",
    enable:
      "The daily Athena order import starts loading this partner's files again, including every file that arrived while it was disabled.",
  },
  NONE: {
    disable:
      "This partner has no inbound job. Outgoing reports have their own switch below.",
    enable:
      "This partner has no inbound job. Outgoing reports have their own switch below.",
  },
};

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div className="col-12 md:col-6 lg:col-4">
      <div className="text-600 text-sm mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function RuleCard({
  mapping,
  payerNames,
}: {
  mapping: ISftpPartnerMappingRule;
  payerNames: Record<string, string>;
}) {
  const { rule, provider } = mapping;
  const pos = Array.isArray(rule.placeOfServiceFilter)
    ? (rule.placeOfServiceFilter as string[])
    : [];
  return (
    <div className="p-3 border-1 surface-border border-round">
      <div className="flex flex-wrap align-items-center gap-2">
        <span className="font-semibold">{claimTypeLabel(rule.claimType)}</span>
        <span className="text-600">→</span>
        {provider?.found ? (
          <span>
            {provider.name}
            {provider.npi ? ` · NPI ${provider.npi}` : ""}
            {provider.specialty ? ` · ${humanizeEnum(provider.specialty)}` : ""}
          </span>
        ) : (
          <span className="flex align-items-center gap-2">
            <code>{provider?.id ?? "(no provider)"}</code>
            <Tag
              severity="danger"
              value={provider ? "Provider not found" : "No provider set"}
            />
          </span>
        )}
      </div>
      <div className="text-sm mt-1">
        Place of service: {pos.length ? pos.join(", ") : "all"}
      </div>
      <SftpRuleSettings rule={rule} payerNames={payerNames} />
    </div>
  );
}

const conversionStatusBody = (c: ISftpPartnerRecentConversion) =>
  c.claimProcessingStatus ? humanizeEnum(c.claimProcessingStatus) : "—";

const conversionClaimTypeBody = (c: ISftpPartnerRecentConversion) =>
  c.claimType ? claimTypeLabel(c.claimType) : "—";

const conversionCreatedBody = (c: ISftpPartnerRecentConversion) =>
  formatDateTime(c.createdAt);

function PartnerBody({ partner }: { partner: ISftpPartnerDetail }) {
  const toastRef = useContext(ToastContext);
  const navigate = useNavigate();
  const [setEnabled, { isLoading }] = useSetSftpPartnerEnabledMutation();
  const nextEnabled = !partner.enabled;
  const isEdi = partner.ingestKind === "EDI";
  const toggleMessages =
    TOGGLE_MESSAGES[partner.ingestKind] ?? TOGGLE_MESSAGES.EDI;

  const onToggle = () => {
    confirmDialog({
      header: nextEnabled
        ? `Enable ${partner.slug}?`
        : `Disable ${partner.slug}?`,
      icon: "pi pi-exclamation-triangle",
      message: nextEnabled ? toggleMessages.enable : toggleMessages.disable,
      acceptLabel: nextEnabled ? "Enable" : "Disable",
      rejectLabel: "Cancel",
      acceptClassName: nextEnabled ? undefined : "p-button-danger",
      accept: async () => {
        try {
          await setEnabled({
            slug: partner.slug,
            enabled: nextEnabled,
          }).unwrap();
          toastRef?.current?.show({
            severity: "success",
            summary: nextEnabled ? "Partner enabled" : "Partner disabled",
            detail: `${partner.slug} is now ${
              nextEnabled ? "enabled" : "disabled"
            }.`,
            life: 5000,
          });
        } catch (error) {
          toastRef?.current?.show({
            severity: "error",
            summary: nextEnabled
              ? "Couldn't enable the partner"
              : "Couldn't disable the partner",
            detail: sftpApiErrorMessage(error, "Please try again."),
            life: 8000,
          });
        }
      },
    });
  };

  return (
    <>
      <div className="flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h1 className="mt-0 mb-2">{partner.slug}</h1>
          <div className="flex flex-wrap align-items-center gap-2">
            <SftpAccountName
              accountId={partner.accountId}
              accountName={partner.accountName}
            />
            <span className="text-600">·</span>
            <span>{ingestModeLabel(partner.ingestMode)}</span>
            <span className="text-600">·</span>
            <span>{ingestKindLabel(partner.ingestKind)}</span>
            <SftpStatusTag enabled={partner.enabled} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            label="Edit"
            icon="pi pi-pencil"
            className="p-button-outlined"
            onClick={() => navigate(sftpPartnerEditPath(partner.slug))}
            data-testid="sftp-partner-edit"
          />
          <Button
            label={partner.enabled ? "Disable" : "Enable"}
            icon={partner.enabled ? "pi pi-pause" : "pi pi-play"}
            className={
              partner.enabled ? "p-button-danger p-button-outlined" : ""
            }
            onClick={onToggle}
            loading={isLoading}
            data-testid="sftp-partner-toggle"
          />
        </div>
      </div>

      <Card title="Overview" className="mb-3">
        <div className="grid">
          <Field label="S3 prefix">
            <code>{partner.s3Prefix}</code>
          </Field>
          <Field label="Account">
            <SftpAccountName
              accountId={partner.accountId}
              accountName={partner.accountName}
            />
          </Field>
          <Field label="Mode">{ingestModeLabel(partner.ingestMode)}</Field>
          <Field label="Kind">{ingestKindLabel(partner.ingestKind)}</Field>
          <Field label="Files processed">{partner.filesProcessed}</Field>
          <Field label="Last file processed">
            {partner.lastProcessedAt
              ? formatDateTime(partner.lastProcessedAt)
              : "Never"}
          </Field>
          <Field label="Created">{formatDateTime(partner.createdAt)}</Field>
          <Field label="Updated">{formatDateTime(partner.updatedAt)}</Field>
        </div>
      </Card>

      {/* Include tokens, mapping rules and conversions only exist for EDI partners. */}
      {isEdi ? (
        <>
          <Card title="Include tokens" className="mb-3">
            {partner.includeTokens.length ? (
              <div className="flex flex-wrap gap-2">
                {partner.includeTokens.map((token) => (
                  <Chip key={token} label={token} />
                ))}
              </div>
            ) : (
              <span>All files (no filter)</span>
            )}
          </Card>

          <Card title="Mapping rules" className="mb-3">
            {partner.mappingRules.length ? (
              <div className="flex flex-column gap-2">
                {partner.mappingRules.map((mapping, i) => (
                  <RuleCard
                    // Rules have no id; their order is the stored order and never changes here.
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    mapping={mapping}
                    payerNames={partner.payerNames ?? {}}
                  />
                ))}
              </div>
            ) : (
              <span>No mapping rules.</span>
            )}
          </Card>

          <Card title="Recent conversions" className="mb-3">
            <DataTable
              value={partner.recentConversions}
              dataKey="id"
              emptyMessage="No conversions yet."
              responsiveLayout="scroll"
            >
              <Column header="Created" body={conversionCreatedBody} />
              <Column header="Conversion" field="displayName" />
              <Column header="Claim type" body={conversionClaimTypeBody} />
              <Column header="Claim processing" body={conversionStatusBody} />
            </DataTable>
          </Card>
        </>
      ) : null}

      <OutboundReportsCard partner={partner} />
    </>
  );
}

// SuperAdmin page for one SFTP partner (`/integrations/sftp/:slug`). Edit opens the partner form;
// Enable / Disable flips `enabled`; the Outgoing reports card manages files we write for the partner.
export function SftpPartnerDetail() {
  const { slug = "" } = useParams();
  useDocumentTitle(`SFTP · ${slug}`);
  const { data, isLoading, isError } = useGetSftpPartnerQuery(slug, {
    skip: !slug,
  });
  const partner = data?.data;

  let body;
  if (isLoading) body = <ProgressSpinner />;
  else if (isError || !partner)
    body = (
      <Message
        severity="error"
        text="Couldn't load this SFTP partner. It may not exist."
      />
    );
  else body = <PartnerBody partner={partner} />;

  return (
    <div className="p-4">
      <Link to={SFTP_TAB_PATH} className="inline-block mb-3">
        <i className="pi pi-arrow-left mr-2" />
        SFTP partners
      </Link>
      {body}
    </div>
  );
}
